import React from 'react';
import { BiSolidPlaneAlt } from 'react-icons/bi';
import Amenities from '../ui/Amenities';

const FlightDetailsCard = ({ fareData, flightData, onClose, onShowDetails }) => {
  if (!flightData || !fareData) {
    return null;
  }

  // Format date from flight data or use current date
  const formatDate = () => {
    return "Monday, August 25, 2025"; // You can make this dynamic based on search params
  };

  // Get fare class name based on fare type
  const getFareClassName = () => {
    return fareData.type || "Premium Economy";
  };

  // Get header text based on fare type (could be DEPART, RETURN, etc.)
  const getHeaderText = () => {
    return "DEPART"; // This could be made dynamic based on flight leg
  };

  // Handle details button click
  const handleDetailsClick = () => {
    if (onShowDetails) {
      onShowDetails(flightData);
    }
  };

  // Handle change button click
  const handleChangeClick = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="bg-white rounded mb-6 border border-gray-300">
      <div tabIndex="-1" className="flex-1">
        <div className="flex flex-wrap p-5 pb-2.5 pl-5 font-sans">
          {/* Header row with DEPART, date, and action buttons */}
          <div className="w-full flex justify-between items-start mb-4">
            <div>
              <span className="text-sm font-semibold text-[#316833]">{getHeaderText()}</span>
              <span className="text-sm text-[#627a88] ml-2">{formatDate()}</span>
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={handleDetailsClick}
                className="text-[#0078d2] hover:underline text-sm font-normal"
              >
                Details
                <span className="sr-only">, for {flightData.departureCode} to {flightData.arrivalCode}, departing at {flightData.departureTime} {flightData.stops}</span>
              </button>
              <span className="text-gray-400">|</span>
              <button 
                onClick={handleChangeClick}
                className="text-[#0078d2] hover:underline text-sm font-normal"
              >
                Change
                <span className="sr-only">, for {flightData.departureCode} to {flightData.arrivalCode}, departing at {flightData.departureTime} {flightData.stops}</span>
              </button>
            </div>
          </div>

          {/* Origin */}
          <div className="flex-shrink-0 text-start">
            <div className="text-lg font-normal text-[#50657c]">{flightData.departureCode}</div>
            <div className="text-2xl font-bold text-[#181e25]">
              {flightData.departureTime.split(' ')[0]} <span className="text-sm font-bold">{flightData.departureTime.split(' ')[1]}</span>
            </div>
          </div>
          
          {/* Arrow */}
          <div aria-hidden="true" className="flex items-center justify-center flex-1 max-w-[8.33%]">
            <span className="text-xl text-gray-600">→</span>
          </div>
          
          {/* Destination */}
          <div className="flex-1 max-w-[25%] text-center">
            <div className="text-lg font-normal text-[#50657c]">{flightData.arrivalCode}</div>
            <div className="text-2xl font-bold text-[#181e25]">
              {flightData.arrivalTime.split(' ')[0]} <span className="text-sm font-bold">{flightData.arrivalTime.split(' ')[1]}</span>
              {flightData.arrivalTime.includes('+') && <span className="text-sm">+1</span>}
            </div>
          </div>
          
          {/* Duration */}
          <div className="flex-1 max-w-[16.67%] text-center font-medium text-gray-800">{flightData.duration}</div>
          
          {/* Stops */}
          <div className="flex-1 max-w-[25%] text-center text-blue-600 cursor-pointer hover:underline">
            <span className="sr-only">Opens {flightData.stops} details for {flightData.departureCode} to {flightData.arrivalCode}, departing at {flightData.departureTime}</span>
            {flightData.stops}
          </div>
          
          {/* Cabin class */}
          <div className="w-full mt-4 mb-2">
            <div className="text-[#181e25] font-medium">{getFareClassName()}</div>
          </div>

          {/* Flight segments - dynamic from flight data */}
          {flightData.segments && flightData.segments.map((segment, index) => (
            <div key={index} className="w-full">
              <div className="flex items-start mb-2 text-sm py-1">
                <div className="mr-2">
                  <BiSolidPlaneAlt aria-hidden="true" className="text-[#333333]" size={18} />
                </div>
                <div className="leading-relaxed">
                  <span className="text-[#181e25] font-thin">{segment.route}</span>
                  <span className="text-[#181e25] mx-1">•</span>
                  <span className="text-[#181e25]"> {segment.flight} </span>
                  <span className="text-[#181e25] mx-1">•</span>
                  <span className="text-[#181e25]"> {segment.aircraft} </span>
                  {segment.operator && (
                    <div className="mt-0.5 mb-0 italic text-xs text-[#333333]">
                      {segment.operator}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Amenities for this specific segment */}
              {segment.amenities && segment.amenities.length > 0 && (
                <div className="w-full mt-1 mb-2 ml-6">
                  <Amenities amenities={segment.amenities} />
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Alerts - dynamic from flight data */}
        {flightData.alerts && flightData.alerts.length > 0 && (
          <div className="px-5 py-1.5  flex items-center gap-2  ">
            <span className="text-red-500 flex-shrink-0 mt-0.5">⚠</span>
            <span className="text-red-500 text-[12px]">
              {flightData.alerts.map((alert, index) => (
                <span key={index}>
                  {alert}
                  {index < flightData.alerts.length - 1 && <span>, </span>}
                </span>
              ))}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlightDetailsCard;